import React, { useEffect } from "react";
import LoadingScreen from "./components/LoadingScreen";

// Hide native status bar when running in Capacitor
async function initCapacitor() {
  try {
    const { StatusBar } = await import("@capacitor/status-bar");
    await StatusBar.hide();
  } catch { }
}
initCapacitor();

// Signal the custom loader that the app is ready
if (typeof window.crescendoFinish === "function") {
  window.crescendoFinish();
}

class GlobalErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", background: "#080808", display: "flex", alignItems: "center", justifyContent: "center", padding: 32, fontFamily: "monospace" }}>
          <div style={{ textAlign: "center" }}>
            <p style={{ color: "#EF4444", fontSize: 14, marginBottom: 8 }}>App error</p>
            <p style={{ color: "#52525B", fontSize: 11, marginBottom: 16 }}>{this.state.error?.message}</p>
            <pre style={{ color: "#3A3A3A", fontSize: 9, textAlign: "left", maxWidth: 400, overflow: "auto" }}>{this.state.error?.stack?.slice(0, 500)}</pre>
            <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: "8px 16px", borderRadius: 8, background: "#5A90F5", color: "white", border: "none", cursor: "pointer", fontSize: 12 }}>Reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { WorkspaceProvider } from "./context/WorkspaceContext";
import { SettingsProvider } from "./context/SettingsContext";
import { api } from "./lib/api";
import { ClassesProvider } from "./context/ClassesContext";
import { TagsProvider } from "./context/TagsContext";
import { FoldersProvider } from "./context/FoldersContext";
import { TemplatesProvider } from "./context/TemplatesContext";
import { KeyPairsProvider } from "./context/KeyPairsContext";
import { ShorthandProvider } from "./context/ShorthandContext";
import { GoalsProvider } from "./context/GoalsContext";
import { DocumentsProvider } from "./context/DocumentsContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import OAuthCallback from "./pages/OAuthCallback";
import Dashboard from "./pages/Dashboard";
import AdminPanel from "./pages/AdminPanel";
import DevLab from './pages/DevLab';
import Analytics from './pages/Analytics';
import ProgressBoards from './pages/ProgressBoards';
import Capture from './pages/Capture';
import Commitments from './pages/Commitments';
import Directory from './pages/Directory';
import ABBoard from './pages/ABBoard';
import Courses from './pages/Courses';
import HandoffBrief from './pages/HandoffBrief';
import Resources from './pages/Resources';
import Compass from './pages/Compass';
import Review from './pages/Review';
import Drift from './pages/Drift';
import Tension from './pages/Tension';
import BuoyBoard from './pages/BuoyBoard';
import KeybindSettings from './pages/KeybindSettings';
import BusinessAdmin from "./pages/BusinessAdmin";
import Library from "./pages/Library";
import Landing from "./pages/Landing";
import ProjectHome from "./pages/ProjectHome";
import { ProjectHomeErrorBoundary } from "./pages/ProjectHome";
import Downloads from "./pages/Downloads";
import ShareView from "./components/ShareView";
import DocShareView from "./components/DocShareView";
import PricingPage from "./pages/Pricing";
import BillingSuccess from "./pages/BillingSuccess";
import SupportCenter from "./pages/SupportCenter";
import { EntitlementsProvider } from "./context/EntitlementsContext";
import { PaywallGate } from "./components/PaywallGate";
import { DocRecoveryToast } from "./components/DocRecoveryToast";
import { applyCalendarBgVars, syncBackgroundFromServer, clearWallpaper } from "./components/WorkspaceSettings";
import { Toaster } from "./components/ui/sonner";
import { ConfirmDialogHost } from "./components/ConfirmDialog";
import { Loader2 } from "lucide-react";

// Detects if running as installed PWA (standalone) or in-app browser
const isPWA = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

function RootRoute() {
  const { user } = useAuth();
  // PWA or logged in → skip landing, go straight to app
  if (isPWA() || user) {
    return <Navigate to={user ? "/projects" : "/login"} replace />;
  }
  return <Landing />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <LoadingScreen />;
  }
  if (!user && !location.state?.user) return <Navigate to="/login" replace />;
  return children;
}

function DashboardShell() {
  const { user } = useAuth();
  const userId = user?.user_id;

  // Wallpaper: load for this user, clear when they log out / switch account.
  // DashboardShell is keyed by user_id so it re-mounts on account change.
  useEffect(() => {
    // Server is authoritative (cross-device). Falls back to user-scoped localStorage.
    syncBackgroundFromServer(api, userId);
    const onChange = () => applyCalendarBgVars(userId);
    window.addEventListener("crescendo:calsettings-changed", onChange);
    return () => {
      window.removeEventListener("crescendo:calsettings-changed", onChange);
      clearWallpaper(); // clear when this user's session unmounts
    };
  }, [userId]);

  // key by user.user_id so switching account fully re-mounts providers + refetches.
  return (
    <WorkspaceProvider key={user?.user_id}>
      <ClassesProvider>
        <TagsProvider>
        <FoldersProvider>
          <TemplatesProvider>
            <KeyPairsProvider>
            <ShorthandProvider>
            <GoalsProvider>
              <DocumentsProvider>
                <Dashboard />
              </DocumentsProvider>
            </GoalsProvider>
            </ShorthandProvider>
            </KeyPairsProvider>
          </TemplatesProvider>
        </FoldersProvider>
        </TagsProvider>
      </ClassesProvider>
    </WorkspaceProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  // Handle both standard Google OAuth (?code=) and legacy Emergent (#session_id=)
  if (location.hash?.includes("session_id=") || location.pathname === "/auth/callback") {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/downloads" element={<Downloads />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/oauth-callback" element={<OAuthCallback />} />
      <Route path="/share/doc/:token" element={<DocShareView />} />
      <Route path="/share/:token" element={<ShareView />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/support" element={<SupportCenter />} />
      <Route path="/billing/success" element={
        <ProtectedRoute><BillingSuccess /></ProtectedRoute>
      } />
      <Route path="/projects" element={
        <ProtectedRoute>
          <WorkspaceProvider>
            <ProjectHomeErrorBoundary>
              <ProjectHome />
            </ProjectHomeErrorBoundary>
          </WorkspaceProvider>
        </ProtectedRoute>
      } />
      <Route path="/app" element={
        <ProtectedRoute>
          <DashboardShell />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanel />
        </ProtectedRoute>
      } />
      <Route path="/dev" element={<ProtectedRoute><DevLab /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><WorkspaceProvider><Analytics /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/boards" element={<ProtectedRoute><WorkspaceProvider><ProgressBoards /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/capture" element={<ProtectedRoute><WorkspaceProvider><Capture /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/commitments" element={<ProtectedRoute><WorkspaceProvider><Commitments /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/directory" element={<ProtectedRoute><WorkspaceProvider><Directory /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/ab" element={<ProtectedRoute><WorkspaceProvider><ABBoard /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/courses" element={<ProtectedRoute><WorkspaceProvider><Courses /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/handoff" element={<ProtectedRoute><WorkspaceProvider><HandoffBrief /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/compass" element={<ProtectedRoute><WorkspaceProvider><Compass /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/review" element={<ProtectedRoute><WorkspaceProvider><Review /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/drift" element={<ProtectedRoute><WorkspaceProvider><Drift /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/tension" element={<ProtectedRoute><WorkspaceProvider><Tension /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/buoy" element={<ProtectedRoute><WorkspaceProvider><BuoyBoard /></WorkspaceProvider></ProtectedRoute>} />
      <Route path="/keybinds" element={<ProtectedRoute><KeybindSettings /></ProtectedRoute>} />
      <Route path="/business" element={
        <ProtectedRoute>
          <WorkspaceProvider>
            <BusinessAdmin />
          </WorkspaceProvider>
        </ProtectedRoute>
      } />
      <Route path="/library" element={
        <ProtectedRoute>
          <WorkspaceProvider>
            <Library />
          </WorkspaceProvider>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Click-to-expand toasts (user request: "allow users to click
  // notifications that overflow to see full toast message"). Sonner doesn't
  // expose a direct hook so we delegate via the document.
  useEffect(() => {
    const onClick = (e) => {
      const t = e.target.closest?.(".crescendo-toast[data-sonner-toast]");
      if (!t) return;
      // Don't toggle when clicking the close (X) button.
      if (e.target.closest?.("[data-close-button],.crescendo-toast-close")) return;
      t.classList.toggle("crescendo-toast-expanded");
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return (
    <div className="App" data-app-root>
      <BrowserRouter>
        <SettingsProvider>
          <AuthProvider>
            <EntitlementsProvider>
              <AppRouter />
              <Toaster />
              <ConfirmDialogHost />
              <PaywallGate />
              <DocRecoveryToast />
            </EntitlementsProvider>
          </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

function AppWithBoundary() {
  return <GlobalErrorBoundary><App /></GlobalErrorBoundary>;
}
export default AppWithBoundary;
